/**
 * SYND-21 — the homepage's first batch, as the server renders it.
 *
 * The SPA builds its All-properties list in batches (pages/Home.tsx): 20
 * marketplace rows from the newest page of `listings` and the first 10 rows of
 * `syndicated_public` (`indexed_at desc, id desc`), composed by
 * lib/search/feedBatches.ts into five blocks of four marketplace cards (tier
 * band, then newest) and two indexed cards (newest indexed first). The server
 * render must show the SAME 30 cards in the SAME order, or the grid visibly
 * reshuffles the moment the SPA hydrates over it.
 *
 * So the server does not have a composer of its own. It hands the rows it
 * fetched — in the order the server returned them, which is the order the SPA
 * receives them in — to the very `appendBatch` the SPA calls, with the same
 * tiers (`agents_public.tier`, read the way the neighbourhood hubs read it) and
 * the same timestamps. What comes back is a list of (source, id) entries:
 * small enough to serialise into the page, and mapped to card views at render
 * time by `homeFirstBatchViews`.
 *
 * WHERE THE TWO CAN STILL DIFFER, and why each is acceptable:
 *   - A failed read on one side: the server shows the other half alone, the
 *     SPA shows whatever its own requests return.
 *   - No marketplace rows at all: the SPA's first batch is then 20 indexed
 *     rows (the feed's indexed-only rule); the server fetched 10 and shows 10.
 *   - An agent with no tier whose name is one of the legacy mock names in
 *     getAgentTierFromProperty's fallback table: the SPA ranks it by that
 *     table, the server as Ignite. The table exists for mock data only.
 *   - The page cache (`s-maxage=60, stale-while-revalidate=300`): a listing
 *     published, or a row indexed, inside that window is in the SPA's first
 *     batch and not yet in the cached HTML.
 *
 * Pure: no React, no fetch, no env — so it runs under `npx tsx` in tests, and
 * the parity test can feed the SPA's mapping and this one the same rows.
 */

import { appendBatch, batchComposition } from '../../../lib/search/feedBatches';
import type { ListingTier } from '../../../lib/search/resultRanking';
import type { SyndicatedCardRow } from '../../../lib/api/syndicatedQuery';
import {
  listingViewFromSsrRow,
  listingViewFromSyndicated,
  type HomeListingSource,
  type HomeListingView,
  type SsrListingLike,
} from './listingView';

/** A marketplace row as the composer needs it: who listed it, and when. */
export interface FirstBatchListingRow {
  id: string;
  agent_id: string | null;
  created_at: string;
}

/** One card of the first batch: which table, which row. */
export interface FirstBatchEntry {
  source: HomeListingSource;
  id: string;
}

/**
 * The first batch, composed exactly as the SPA composes it.
 *
 * `marketplace` and `indexed` must be in the order their queries returned them
 * (`created_at desc` and `indexed_at desc, id desc`): ranking ties keep their
 * input order, and the indexed half is never re-sorted.
 */
export function composeHomeFirstBatch(
  marketplace: readonly FirstBatchListingRow[],
  tiers: ReadonlyMap<string, ListingTier>,
  indexed: readonly Pick<SyndicatedCardRow, 'id' | 'indexed_at'>[]
): FirstBatchEntry[] {
  // The SPA's first batch: marketplace exhausted only when there is none.
  const want = batchComposition(marketplace.length === 0);
  const incoming = [
    ...marketplace.slice(0, want.marketplace).map((row) => ({
      kind: 'listing' as const,
      tier: (row.agent_id ? tiers.get(row.agent_id) : undefined) ?? ('Ignite' as const),
      // The SPA's own expression (getPropertyTimestamp, lib/search/propertyRanking.ts).
      timestamp: new Date(row.created_at).getTime(),
      entry: { source: 'listing' as const, id: row.id },
    })),
    ...indexed.slice(0, want.syndicated).map((row) => ({
      kind: 'syndicated' as const,
      timestamp: Date.parse(row.indexed_at),
      entry: { source: 'syndicated' as const, id: row.id },
    })),
  ];
  return appendBatch([], incoming, (row) => `${row.entry.source}:${row.entry.id}`).map(
    (row) => row.entry
  );
}

/** What `homeFirstBatchViews` reads from the page data. */
export interface HomeFirstBatchData {
  listings: readonly (SsrListingLike & { id: string })[];
  feedIndexed?: readonly SyndicatedCardRow[];
  feedFirstBatch?: readonly FirstBatchEntry[];
}

/**
 * The first batch as card views, for the server-rendered grid.
 *
 * `feedFirstBatch` is absent from a page cached before SYND-21 shipped; such a
 * page renders its marketplace rows in their stored order, as it always did.
 * An entry whose row is missing is skipped rather than rendered empty.
 */
export function homeFirstBatchViews(data: HomeFirstBatchData): HomeListingView[] {
  const entries: readonly FirstBatchEntry[] =
    data.feedFirstBatch ?? data.listings.map((row) => ({ source: 'listing' as const, id: row.id }));
  const listingsById = new Map(data.listings.map((row) => [row.id, row]));
  const indexedById = new Map((data.feedIndexed ?? []).map((row) => [row.id, row]));
  const views: HomeListingView[] = [];
  for (const entry of entries) {
    if (entry.source === 'listing') {
      const row = listingsById.get(entry.id);
      if (row) views.push(listingViewFromSsrRow(row));
    } else {
      const row = indexedById.get(entry.id);
      if (row) views.push(listingViewFromSyndicated(row));
    }
  }
  return views;
}
